import React, { useState, useEffect } from 'react';
import { db, generateLocalId } from './db';
import { Plus, Trash2, Edit2, X, Check, Tag, Layers, Database } from 'lucide-react';
import client from './api/client';

function CategoryManager({ darkMode, refreshCategories }) {
  const [activeTab, setActiveTab] = useState('categories'); // 'categories' or 'tags'
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => {
    fetchItems();
  }, [activeTab]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const table = activeTab === 'categories' ? db.categories : db.tags;
      const allData = await table.toArray();
      const filtered = allData.filter(item => item.sync_status !== 'pending_delete');
      setItems(filtered);
    } catch (error) {
      console.error(`Error fetching ${activeTab} locally:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncFromItems = async () => {
    if (!window.confirm(`기존 물품 데이터에서 ${activeTab === 'categories' ? '카테고리' : '태그'} 정보를 추출하여 DB에 등록하시겠습니까?\n(온라인 환경에서만 수행 가능)`)) return;
    
    setSyncing(true);
    setSyncResult(null);
    try {
      const endpoint = activeTab === 'categories' ? '/admin/categories/sync' : '/admin/tags/sync';
      const response = await client.post(endpoint);
      setSyncResult(response.data);
      
      // Perform pull sync to update local Dexie
      const lastSyncTime = localStorage.getItem('last_sync_time') || null;
      const pullRes = await client.get('/sync/pull', {
        params: { last_sync_time: lastSyncTime || undefined }
      });
      
      await db.transaction('rw', [db.categories, db.tags], async () => {
        if (activeTab === 'categories') {
          for (const cat of pullRes.data.categories) {
            await db.categories.put({ id: cat.id, name: cat.name, sync_status: 'synced', updated_at: new Date().toISOString() });
          }
        } else {
          for (const t of pullRes.data.tags || []) {
            await db.tags.put({ id: t.id, name: t.name, sync_status: 'synced', updated_at: new Date().toISOString() });
          }
        }
      });

      fetchItems();
      if (refreshCategories) refreshCategories();
    } catch (error) {
      console.error(`Error syncing ${activeTab}:`, error);
      alert('일괄 불러오기 중 오류가 발생했습니다. 네트워크 연결 및 관리자 권한을 확인하세요.');
    } finally {
      setSyncing(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newItemName.trim()) return;

    try {
      const table = activeTab === 'categories' ? db.categories : db.tags;
      const prefix = activeTab === 'categories' ? 'local_cat_' : 'local_tag_';
      
      // Check for exact duplicate in memory
      const allData = await table.toArray();
      const existing = allData.find(item => 
        item.name.toLowerCase() === newItemName.trim().toLowerCase() && 
        item.sync_status !== 'pending_delete'
      );
      
      if (existing) {
        alert(`이미 존재하는 ${activeTab === 'categories' ? '카테고리' : '태그'} 이름입니다.`);
        return;
      }

      await table.put({
        id: generateLocalId(prefix),
        name: newItemName.trim(),
        sync_status: 'pending_upload',
        updated_at: new Date().toISOString()
      });
      
      setNewItemName('');
      fetchItems();
      if (refreshCategories) refreshCategories();
    } catch (error) {
      console.error(`Error adding ${activeTab} locally:`, error);
    }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;

    try {
      const table = activeTab === 'categories' ? db.categories : db.tags;
      
      // Check duplicate in memory
      const allData = await table.toArray();
      const existing = allData.find(item => 
        item.id !== id && 
        item.name.toLowerCase() === editName.trim().toLowerCase() && 
        item.sync_status !== 'pending_delete'
      );

      if (existing) {
        alert('이미 존재하는 이름입니다.');
        return;
      }

      await table.update(id, {
        name: editName.trim(),
        sync_status: 'pending_upload',
        updated_at: new Date().toISOString()
      });
      
      setEditingId(null);
      setEditName('');
      fetchItems();
      if (refreshCategories) refreshCategories();
    } catch (error) {
      console.error(`Error updating ${activeTab} locally:`, error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까? 이 항목을 사용하는 물품이 있을 수 있습니다.')) return;

    try {
      const table = activeTab === 'categories' ? db.categories : db.tags;
      
      // Check if items are referencing this in memory
      if (activeTab === 'categories') {
        const allItems = await db.items.toArray();
        const inUse = allItems.filter(i => i.category_id === id && i.sync_status !== 'pending_delete');
        if (inUse.length > 0) {
          alert('현재 등록된 물품에서 사용 중인 카테고리는 삭제할 수 없습니다.');
          return;
        }
      }

      if (String(id).startsWith('local_')) {
        await table.delete(id);
      } else {
        await table.update(id, {
          sync_status: 'pending_delete',
          updated_at: new Date().toISOString()
        });
      }
      
      fetchItems();
      if (refreshCategories) refreshCategories();
    } catch (error) {
      console.error(`Error deleting ${activeTab} locally:`, error);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditName(item.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const styles = {
    container: {
      padding: '20px',
      color: darkMode ? '#fff' : '#000',
    },
    tabContainer: {
      display: 'flex',
      marginBottom: '20px',
      borderBottom: darkMode ? '1px solid #333' : '1px solid #eee',
    },
    tab: {
      padding: '10px 20px',
      cursor: 'pointer',
      borderBottom: '2px solid transparent',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: darkMode ? '#aaa' : '#666',
    },
    activeTab: {
      borderBottom: '2px solid #007bff',
      color: darkMode ? '#fff' : '#007bff',
      fontWeight: 'bold',
    },
    inputGroup: {
      display: 'flex',
      gap: '10px',
      marginBottom: '20px',
    },
    toolbar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '10px',
      flexWrap: 'wrap',
    },
    input: {
      flex: 1,
      padding: '10px',
      borderRadius: '5px',
      border: darkMode ? '1px solid #444' : '1px solid #ddd',
      backgroundColor: darkMode ? '#333' : '#fff',
      color: darkMode ? '#fff' : '#000',
    },
    button: {
      padding: '10px 20px',
      borderRadius: '5px',
      border: 'none',
      backgroundColor: '#007bff',
      color: '#fff',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
    },
    syncBtn: {
      padding: '8px 12px',
      borderRadius: '8px',
      border: darkMode ? '1px solid #444' : '1px solid #ddd',
      backgroundColor: darkMode ? '#2a2a2a' : '#fff',
      color: darkMode ? '#fff' : '#333',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '12px',
    },
    syncResult: {
      fontSize: '12px',
      color: darkMode ? '#aaa' : '#666',
    },
    list: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
    },
    item: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '15px',
      borderRadius: '8px',
      backgroundColor: darkMode ? '#2a2a2a' : '#f9f9f9',
      border: darkMode ? '1px solid #333' : '1px solid #eee',
    },
    actionBtn: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '5px',
      color: darkMode ? '#aaa' : '#666',
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={{marginBottom: '20px'}}>태그/카테고리 관리</h2>
      
      <div style={styles.tabContainer}>
        <div 
          style={{...styles.tab, ...(activeTab === 'categories' ? styles.activeTab : {})}}
          onClick={() => setActiveTab('categories')}
        >
          <Layers size={18} />
          카테고리
        </div>
        <div 
          style={{...styles.tab, ...(activeTab === 'tags' ? styles.activeTab : {})}}
          onClick={() => setActiveTab('tags')}
        >
          <Tag size={18} />
          태그
        </div>
      </div>

      <div style={styles.toolbar}>
        <div style={{display: 'flex', flexDirection: 'column', gap: '5px', flex: 1}}>
          <button
            onClick={handleSyncFromItems}
            style={{...styles.syncBtn, width: 'fit-content', opacity: syncing ? 0.6 : 1, backgroundColor: darkMode ? '#1a3a5a' : '#e7f3ff', borderColor: darkMode ? '#2c5282' : '#b2d7ff'}}
            disabled={syncing}
          >
            <Database size={16} color="#3182ce" />
            {syncing ? '데이터 분석 중...' : `기존 물품에서 ${activeTab === 'categories' ? '카테고리' : '태그'} 일괄 추출`}
          </button>
          <p style={{fontSize: '11px', color: darkMode ? '#888' : '#666', margin: '0 5px'}}>
            * 등록된 물품들의 {activeTab === 'categories' ? '카테고리' : '태그'} 텍스트를 분석하여 자동으로 목록에 추가합니다. (온라인 전용)
          </p>
        </div>
        
        {syncResult && (
          <div style={{...styles.syncResult, backgroundColor: darkMode ? '#1c2d1c' : '#f0fff4', padding: '8px 12px', borderRadius: '8px', border: `1px solid ${darkMode ? '#276749' : '#c6f6d5'}`}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '5px', color: darkMode ? '#68d391' : '#2f855a', fontWeight: 'bold', marginBottom: '2px'}}>
              <Check size={14} /> 동기화 완료
            </div>
            신규 생성: {syncResult.created}건 / 물품 연결: {syncResult.linked_items}건
          </div>
        )}
      </div>

      <form onSubmit={handleAdd} style={styles.inputGroup}>
        <input
          type="text"
          value={newItemName}
          onChange={(e) => setNewItemName(e.target.value)}
          placeholder={`새 ${activeTab === 'categories' ? '카테고리' : '태그'} 이름`}
          style={styles.input}
        />
        <button type="submit" style={styles.button}>
          <Plus size={18} />
          추가
        </button>
      </form>

      {loading ? (
        <div>로딩 중...</div>
      ) : (
        <div style={styles.list}>
          {items.map(item => (
            <div key={item.id} style={styles.item}>
              {editingId === item.id ? (
                <div style={{display: 'flex', flex: 1, gap: '10px', alignItems: 'center'}}>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    style={styles.input}
                    autoFocus
                  />
                  <button onClick={() => handleUpdate(item.id)} style={{...styles.actionBtn, color: '#4caf50'}}>
                    <Check size={18} />
                  </button>
                  <button onClick={cancelEdit} style={{...styles.actionBtn, color: '#f44336'}}>
                    <X size={18} />
                  </button>
                </div>
              ) : (
                <>
                  <span style={{fontWeight: 500}}>{item.name}</span>
                  <div style={{display: 'flex', gap: '5px'}}>
                    <button onClick={() => startEdit(item)} style={styles.actionBtn}>
                      <Edit2 size={18} />
                    </button>
                    <button onClick={() => handleDelete(item.id)} style={{...styles.actionBtn, color: '#f44336'}}>
                      <Trash2 size={18} />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
          {items.length === 0 && (
            <div style={{textAlign: 'center', padding: '20px', color: darkMode ? '#666' : '#999'}}>
              등록된 {activeTab === 'categories' ? '카테고리가' : '태그가'} 없습니다.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CategoryManager;
